import { useState, useEffect } from 'react';
import type { NoticeItem, AcknowledgmentEntry, NoticePriority, NoticeType } from './noticeMockData';
import { computeNoticeStatus, buildAudienceDisplay } from './noticeMockData';
import {
    fetchNotices, createNotice, deleteNotice,
    fetchAcknowledgments, sendReminder,
    type NoticeApiRecord, type NoticeAcknowledgment,
} from '../../services/noticeApi';
import { fetchClasses, fetchClassSections, type ClassApiRecord, type SectionApiRecord } from '../../services/classApi';
import { useTranslation } from 'react-i18next';
import './NoticeLedger.css';

/* ══════════════════════════════════════════════
   Helper: map API record → local NoticeItem
   ══════════════════════════════════════════════ */
function mapApiToItem(r: NoticeApiRecord): NoticeItem {
    const audiences = (r.audiences ?? []).map((a) => ({
        class_id: a.class_id,
        section_id: a.section_id,
        class_name: a.school_class?.name || '',
        section_name: a.section?.name || '',
    }));

    return {
        id: r.id,
        title: r.title,
        content: r.content,
        type: r.type,
        priority: r.priority,
        is_school_wide: r.is_school_wide,
        requires_acknowledgment: r.requires_acknowledgment,
        published_at: r.published_at,
        expires_at: r.expires_at,
        created_at: r.created_at,
        audience_display: buildAudienceDisplay(r.is_school_wide, audiences),
        status: computeNoticeStatus(r.published_at, r.expires_at),
        acknowledgments_count: r.acknowledgments_count ?? 0,
        total_audience_count: r.total_audience_count ?? 0,
        audiences,
    };
}

function mapAckToEntry(a: NoticeAcknowledgment): AcknowledgmentEntry {
    const firstName = a.user?.first_name || '';
    const lastName = a.user?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim() || 'Unknown User';
    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '??';
    return {
        id: a.id,
        user_id: a.user_id,
        user_name: fullName,
        user_role: a.user?.role || 'user',
        avatar_initials: initials,
        acknowledged_at: a.acknowledged_at,
    };
}

/* ══════════════════════════════════════════════
   Component
   ══════════════════════════════════════════════ */
export default function NoticeLedger() {
    const { t: _t } = useTranslation();

    /* ── Data state ── */
    const [notices, setNotices] = useState<NoticeItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    /* ── Drawer state ── */
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    /* ── Acknowledgment drawer state ── */
    const [ackDrawerOpen, setAckDrawerOpen] = useState(false);
    const [ackNotice, setAckNotice] = useState<NoticeItem | null>(null);
    const [ackEntries, setAckEntries] = useState<AcknowledgmentEntry[]>([]);
    const [ackLoading, setAckLoading] = useState(false);
    const [ackSearch, setAckSearch] = useState('');
    const [reminding, setReminding] = useState(false);

    /* ── Form state ── */
    const [formTitle, setFormTitle] = useState('');
    const [formContent, setFormContent] = useState('');
    const [formType, setFormType] = useState<NoticeType>('general');
    const [formPriority, setFormPriority] = useState<NoticePriority>('normal');
    const [formIsSchoolWide, setFormIsSchoolWide] = useState(true);
    const [formRequiresAck, setFormRequiresAck] = useState(false);
    const [formPublishedAt, setFormPublishedAt] = useState('');
    const [formExpiresAt, setFormExpiresAt] = useState('');
    const [formAudiences, setFormAudiences] = useState<{ class_id: string; section_id: string }[]>([]);

    /* ── Dropdown data ── */
    const [classes, setClasses] = useState<ClassApiRecord[]>([]);
    const [sectionMap, setSectionMap] = useState<Record<string, SectionApiRecord[]>>({});

    /* ── Load notices + classes on mount ── */
    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetchNotices({ perPage: 100 }),
            fetchClasses({ perPage: 100 }),
        ]).then(([noticeRes, classesRes]) => {
            setNotices(noticeRes.data.map(mapApiToItem));
            setClasses(classesRes.data);
        }).catch(() => {
            setErrorMsg('Failed to load notices.');
            setTimeout(() => setErrorMsg(''), 5000);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    /* ── Load sections when a class is selected in audience rows ── */
    const loadSections = async (classId: string) => {
        if (sectionMap[classId]) return;
        try {
            const res = await fetchClassSections(classId, { perPage: 100 });
            setSectionMap((prev) => ({ ...prev, [classId]: res.data }));
        } catch {
            // silent
        }
    };

    /* ── Helpers ── */
    const formatDate = (d: string | null) => {
        if (!d) return '—';
        const dateStr = d.includes('T') ? d.split('T')[0] : d;
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
        if (type === 'error') {
            setErrorMsg(msg);
        } else {
            setSuccessMsg(msg);
        }
        setTimeout(() => {
            setSuccessMsg('');
            setErrorMsg('');
        }, 5000);
    };

    /* ── Open creation drawer ── */
    const openDrawer = () => {
        setFormTitle('');
        setFormContent('');
        setFormType('general');
        setFormPriority('normal');
        setFormIsSchoolWide(true);
        setFormRequiresAck(false);
        setFormPublishedAt('');
        setFormExpiresAt('');
        setFormAudiences([]);
        setErrorMsg('');
        setDrawerOpen(true);
    };

    /* ── Submit notice ── */
    const handleSubmit = async () => {
        if (!formTitle.trim() || !formContent.trim()) return;
        const schoolId = localStorage.getItem('school_id');
        if (!schoolId) {
            setErrorMsg('No school selected. Please log in again.');
            return;
        }
        setSaving(true);
        setErrorMsg('');

        try {
            const apiRecord = await createNotice({
                school_id: schoolId,
                title: formTitle,
                content: formContent,
                type: formType,
                priority: formPriority,
                is_school_wide: formIsSchoolWide,
                requires_acknowledgment: formRequiresAck,
                published_at: formPublishedAt || null,
                expires_at: formExpiresAt || null,
                audiences: formIsSchoolWide
                    ? []
                    : formAudiences
                          .filter((a) => a.class_id)
                          .map((a) => ({
                              class_id: a.class_id,
                              section_id: a.section_id || null,
                          })),
            });

            const newItem = mapApiToItem(apiRecord);
            // Enrich audience display from form selections if API doesn't return nested relations
            if (!apiRecord.audiences?.length && !formIsSchoolWide) {
                newItem.audience_display = buildAudienceDisplay(
                    false,
                    formAudiences.map((a) => ({
                        class_name: classes.find((c) => c.id === a.class_id)?.name || '',
                        section_name: a.section_id ? sectionMap[a.class_id]?.find((s) => s.id === a.section_id)?.name || '' : '',
                    }))
                );
            }

            setNotices((prev) => [newItem, ...prev]);
            setDrawerOpen(false);
            showToast('Notice published successfully!');
        } catch (err: unknown) {
            const apiErr = err as { errors?: Record<string, string[]>; message?: string };
            if (apiErr.errors) {
                const messages = Object.values(apiErr.errors).flat();
                setErrorMsg(messages.join(' '));
            } else {
                setErrorMsg(apiErr.message || 'Failed to create notice. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    /* ── Delete notice ── */
    const handleDelete = async (id: string) => {
        try {
            await deleteNotice(id);
            setNotices((prev) => prev.filter((n) => n.id !== id));
            showToast('Notice removed.');
        } catch {
            showToast('Failed to delete notice.', 'error');
        }
    };

    /* ── Open acknowledgment drawer ── */
    const openAckDrawer = async (notice: NoticeItem) => {
        setAckNotice(notice);
        setAckSearch('');
        setAckDrawerOpen(true);
        setAckLoading(true);

        try {
            const res = await fetchAcknowledgments(notice.id, { perPage: 200 });
            setAckEntries(res.data.map(mapAckToEntry));
        } catch {
            setAckEntries([]);
        } finally {
            setAckLoading(false);
        }
    };

    /* ── Send reminder ── */
    const handleReminder = async () => {
        if (!ackNotice) return;
        setReminding(true);
        try {
            await sendReminder(ackNotice.id);
            showToast('Reminder sent to pending users!');
        } catch {
            showToast('Failed to send reminder.', 'error');
        } finally {
            setReminding(false);
        }
    };

    /* ── Audience form helpers ── */
    const addAudienceRow = () => {
        setFormAudiences((prev) => [...prev, { class_id: '', section_id: '' }]);
    };

    const updateAudienceRow = (index: number, field: 'class_id' | 'section_id', value: string) => {
        setFormAudiences((prev) => {
            const copy = [...prev];
            copy[index] = { ...copy[index], [field]: value };
            if (field === 'class_id') {
                copy[index].section_id = '';
                if (value) loadSections(value);
            }
            return copy;
        });
    };

    const removeAudienceRow = (index: number) => {
        setFormAudiences((prev) => prev.filter((_, i) => i !== index));
    };

    /* ── Filtered ack entries ── */
    const filteredAck = ackSearch
        ? ackEntries.filter((e) => e.user_name.toLowerCase().includes(ackSearch.toLowerCase()))
        : ackEntries;

    const ackReadCount = ackEntries.filter((e) => e.acknowledged_at).length;
    const ackPendingCount = ackEntries.length - ackReadCount;

    const formValid = formTitle.trim().length > 0 && formContent.trim().length > 0;

    /* ── Type / Priority options ── */
    const typeOptions: { value: NoticeType; label: string }[] = [
        { value: 'general', label: 'General' },
        { value: 'academic', label: 'Academic' },
        { value: 'event', label: 'Event' },
        { value: 'emergency', label: 'Emergency' },
        { value: 'fee_update', label: 'Fee Update' },
    ];

    const priorityOptions: { value: NoticePriority; label: string }[] = [
        { value: 'urgent', label: 'Urgent' },
        { value: 'high', label: 'High' },
        { value: 'normal', label: 'Normal' },
        { value: 'low', label: 'Low' },
    ];

    return (
        <div className="notice">
            {/* Toasts */}
            {successMsg && (
                <div className="notice__toast">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="notice__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}
            {errorMsg && !drawerOpen && (
                <div className="notice__toast notice__toast--error">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    <span>{errorMsg}</span>
                    <button className="notice__toast-dismiss" onClick={() => setErrorMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* Header */}
            <div className="notice__header">
                <div className="notice__header-text">
                    <h1 className="notice__title">Notice Board</h1>
                    <p className="notice__subtitle">
                        Publish notices, target audiences, and track acknowledgments.
                    </p>
                </div>
                <button className="notice__add-btn" onClick={openDrawer}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    New Notice
                </button>
            </div>

            {/* Grid */}
            {loading ? (
                <div className="notice__loading">
                    <div className="notice__loader">
                        <span className="notice__loader-dot" />
                        <span className="notice__loader-dot" />
                        <span className="notice__loader-dot" />
                    </div>
                </div>
            ) : notices.length === 0 ? (
                <div className="notice__empty-state">
                    <div className="notice__empty-icon">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
                    </div>
                    <h3 className="notice__empty-title">No Notices Published</h3>
                    <p className="notice__empty-text">Click "New Notice" to create your first announcement.</p>
                </div>
            ) : (
                <div className="notice__grid-wrap">
                    <table className="notice__table">
                        <thead>
                            <tr>
                                <th className="notice__th">Title</th>
                                <th className="notice__th">Type</th>
                                <th className="notice__th">Priority</th>
                                <th className="notice__th">Target Audience</th>
                                <th className="notice__th">Publish Date</th>
                                <th className="notice__th">Status</th>
                                <th className="notice__th">Read Receipts</th>
                                <th className="notice__th notice__th--actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {notices.map((n) => (
                                <tr className="notice__row" key={n.id}>
                                    <td className="notice__td notice__td--title">{n.title}</td>
                                    <td className="notice__td">
                                        <span className={`notice__badge notice__badge--${n.type}`}>{n.type}</span>
                                    </td>
                                    <td className="notice__td">
                                        <span className={`notice__badge notice__badge--${n.priority}`}>{n.priority}</span>
                                    </td>
                                    <td className="notice__td">
                                        {n.is_school_wide ? (
                                            <span className="notice__target-badge notice__target-badge--school-wide">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" /></svg>
                                                School-Wide
                                            </span>
                                        ) : (
                                            <span className="notice__target-badge notice__target-badge--targeted">
                                                {n.audience_display}
                                            </span>
                                        )}
                                    </td>
                                    <td className="notice__td notice__td--date">
                                        {formatDate(n.published_at)}
                                    </td>
                                    <td className="notice__td">
                                        <span className={`notice__badge notice__badge--${n.status}`}>{n.status}</span>
                                    </td>
                                    <td className="notice__td">
                                        {n.requires_acknowledgment ? (
                                            <span className="notice__receipts">
                                                {n.acknowledgments_count} / {n.total_audience_count}
                                            </span>
                                        ) : (
                                            <span className="notice__receipts notice__receipts--na">N/A</span>
                                        )}
                                    </td>
                                    <td className="notice__td">
                                        <div className="notice__actions-cell">
                                            {n.requires_acknowledgment && (
                                                <button
                                                    className="notice__action-btn"
                                                    onClick={() => openAckDrawer(n)}
                                                    title="View Receipts"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                                    Receipts
                                                </button>
                                            )}
                                            <button
                                                className="notice__action-btn notice__action-btn--icon"
                                                onClick={() => handleDelete(n.id)}
                                                title="Delete"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ═══ Creation Drawer ═══ */}
            {drawerOpen && (
                <>
                    <div className="notice__drawer-overlay" onClick={() => setDrawerOpen(false)} />
                    <div className="notice__drawer">
                        <div className="notice__drawer-header">
                            <h2 className="notice__drawer-title">New Notice</h2>
                            <button className="notice__drawer-close" onClick={() => setDrawerOpen(false)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>

                        <div className="notice__drawer-body">
                            {errorMsg && (
                                <div className="notice__toast notice__toast--error" style={{ position: 'static', animation: 'none' }}>
                                    {errorMsg}
                                </div>
                            )}

                            {/* Card 1 — Content */}
                            <div className="notice__drawer-card">
                                <span className="notice__drawer-card-title">Content</span>

                                <div className="notice__field">
                                    <label className="notice__label" htmlFor="noticeTitle">Title *</label>
                                    <input
                                        className="notice__input"
                                        id="noticeTitle"
                                        type="text"
                                        placeholder="e.g. Annual Day Celebration"
                                        maxLength={150}
                                        value={formTitle}
                                        onChange={(e) => setFormTitle(e.target.value)}
                                    />
                                </div>

                                <div className="notice__field-row">
                                    <div className="notice__field">
                                        <label className="notice__label" htmlFor="noticeType">Type</label>
                                        <select
                                            className="notice__select"
                                            id="noticeType"
                                            value={formType}
                                            onChange={(e) => setFormType(e.target.value as NoticeType)}
                                        >
                                            {typeOptions.map((o) => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="notice__field">
                                        <label className="notice__label" htmlFor="noticePriority">Priority</label>
                                        <select
                                            className="notice__select"
                                            id="noticePriority"
                                            value={formPriority}
                                            onChange={(e) => setFormPriority(e.target.value as NoticePriority)}
                                        >
                                            {priorityOptions.map((o) => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="notice__field">
                                    <label className="notice__label" htmlFor="noticeContent">Content *</label>
                                    <textarea
                                        className="notice__textarea"
                                        id="noticeContent"
                                        placeholder="Write notice content here…"
                                        value={formContent}
                                        onChange={(e) => setFormContent(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Card 2 — Audience Selector */}
                            <div className="notice__drawer-card">
                                <span className="notice__drawer-card-title">Audience</span>

                                <div className="notice__toggle-row">
                                    <div className="notice__toggle-info">
                                        <span className="notice__toggle-label">School-Wide Broadcast</span>
                                        <span className="notice__toggle-desc">Send to all students, parents & staff</span>
                                    </div>
                                    <label className="notice__toggle">
                                        <input
                                            type="checkbox"
                                            checked={formIsSchoolWide}
                                            onChange={(e) => {
                                                setFormIsSchoolWide(e.target.checked);
                                                if (e.target.checked) setFormAudiences([]);
                                            }}
                                        />
                                        <span className="notice__toggle-track" />
                                    </label>
                                </div>

                                {!formIsSchoolWide && (
                                    <div className="notice__audience-list">
                                        {formAudiences.map((aud, idx) => (
                                            <div className="notice__audience-row" key={idx}>
                                                <div className="notice__field">
                                                    <label className="notice__label">Class</label>
                                                    <select
                                                        className="notice__select"
                                                        value={aud.class_id}
                                                        onChange={(e) => updateAudienceRow(idx, 'class_id', e.target.value)}
                                                    >
                                                        <option value="">Select Class…</option>
                                                        {classes.map((c) => (
                                                            <option key={c.id} value={c.id}>{c.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="notice__field">
                                                    <label className="notice__label">Section (optional)</label>
                                                    <select
                                                        className="notice__select"
                                                        value={aud.section_id}
                                                        onChange={(e) => updateAudienceRow(idx, 'section_id', e.target.value)}
                                                        disabled={!aud.class_id}
                                                    >
                                                        <option value="">All Sections</option>
                                                        {(sectionMap[aud.class_id] || []).map((s) => (
                                                            <option key={s.id} value={s.id}>{s.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <button
                                                    className="notice__audience-remove"
                                                    onClick={() => removeAudienceRow(idx)}
                                                    title="Remove"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                                </button>
                                            </div>
                                        ))}
                                        <button className="notice__audience-add" onClick={addAudienceRow}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                                            Add Target
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Card 3 — Settings & Scheduling */}
                            <div className="notice__drawer-card">
                                <span className="notice__drawer-card-title">Settings & Scheduling</span>

                                <div className="notice__toggle-row">
                                    <div className="notice__toggle-info">
                                        <span className="notice__toggle-label">Requires Acknowledgment</span>
                                        <span className="notice__toggle-desc">Track who has read this notice</span>
                                    </div>
                                    <label className="notice__toggle">
                                        <input
                                            type="checkbox"
                                            checked={formRequiresAck}
                                            onChange={(e) => setFormRequiresAck(e.target.checked)}
                                        />
                                        <span className="notice__toggle-track" />
                                    </label>
                                </div>

                                <div className="notice__field-row">
                                    <div className="notice__field">
                                        <label className="notice__label" htmlFor="noticePublish">Publish Date</label>
                                        <input
                                            className="notice__input"
                                            id="noticePublish"
                                            type="datetime-local"
                                            value={formPublishedAt}
                                            onChange={(e) => setFormPublishedAt(e.target.value)}
                                        />
                                    </div>
                                    <div className="notice__field">
                                        <label className="notice__label" htmlFor="noticeExpires">Expires At</label>
                                        <input
                                            className="notice__input"
                                            id="noticeExpires"
                                            type="datetime-local"
                                            value={formExpiresAt}
                                            onChange={(e) => setFormExpiresAt(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Sticky footer */}
                        <div className="notice__drawer-footer">
                            <button className="notice__drawer-cancel" onClick={() => setDrawerOpen(false)}>
                                Cancel
                            </button>
                            <button
                                className="notice__drawer-save"
                                onClick={handleSubmit}
                                disabled={!formValid || saving}
                            >
                                {saving ? 'Publishing…' : 'Publish Notice'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* ═══ Acknowledgment Receipts Drawer ═══ */}
            {ackDrawerOpen && (
                <>
                    <div className="notice__drawer-overlay" onClick={() => setAckDrawerOpen(false)} />
                    <div className="notice__drawer">
                        <div className="notice__drawer-header">
                            <h2 className="notice__drawer-title">
                                Receipts — {ackNotice?.title ?? 'Notice'}
                            </h2>
                            <button className="notice__drawer-close" onClick={() => setAckDrawerOpen(false)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>

                        {!ackLoading && (
                            <div className="notice__ack-header-bar">
                                <div className="notice__ack-stats">
                                    <span className="notice__ack-stat--read">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-1px', marginRight: 3 }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                                        {ackReadCount} Read
                                    </span>
                                    <span className="notice__ack-stat--pending">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-1px', marginRight: 3 }}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                        {ackPendingCount} Pending
                                    </span>
                                </div>
                                <button
                                    className="notice__remind-btn"
                                    onClick={handleReminder}
                                    disabled={reminding || ackPendingCount === 0}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
                                    {reminding ? 'Sending…' : 'Send Reminder'}
                                </button>
                            </div>
                        )}

                        <input
                            className="notice__ack-search"
                            type="text"
                            placeholder="Search by name…"
                            value={ackSearch}
                            onChange={(e) => setAckSearch(e.target.value)}
                        />

                        <div className="notice__ack-list">
                            {ackLoading ? (
                                <div className="notice__loading" style={{ padding: '40px 0' }}>
                                    <div className="notice__loader">
                                        <span className="notice__loader-dot" />
                                        <span className="notice__loader-dot" />
                                        <span className="notice__loader-dot" />
                                    </div>
                                </div>
                            ) : filteredAck.length === 0 ? (
                                <div className="notice__ack-empty">
                                    {ackSearch ? 'No results matching your search.' : 'No acknowledgment data available.'}
                                </div>
                            ) : (
                                filteredAck.map((entry) => (
                                    <div className="notice__ack-item" key={entry.id}>
                                        <div className="notice__ack-avatar">{entry.avatar_initials}</div>
                                        <div className="notice__ack-info">
                                            <div className="notice__ack-name">{entry.user_name}</div>
                                            <div className="notice__ack-role">{entry.user_role}</div>
                                        </div>
                                        {entry.acknowledged_at ? (
                                            <span className="notice__ack-status notice__ack-status--read">
                                                Read on {formatDate(entry.acknowledged_at)}
                                            </span>
                                        ) : (
                                            <span className="notice__ack-status notice__ack-status--pending">
                                                Pending
                                            </span>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
